'use client'

import { useState } from 'react'
import { STORAGE_KEYS, setStorageItem } from '@/lib/pwa-storage'
import { readStoredConsent } from '@/lib/cookie-consent-status'
import { clearMarketingAttribution, persistMarketingAttribution } from '@/lib/marketing-attribution'

export type ConsentStatus = 'accepted' | 'rejected' | 'pending' | null

/**
 * Hook para gestionar el consentimiento de cookies
 * Cumple con GDPR - requiere consentimiento explícito antes de cargar cookies de tracking
 */
function getInitialConsentStatus(): ConsentStatus {
  if (typeof window === 'undefined') return null
  return readStoredConsent() ?? 'pending'
}

export function useCookieConsent() {
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>(getInitialConsentStatus)
  const [isLoading] = useState(false)

  /**
   * Verifica si el usuario ha dado consentimiento para analytics
   */
  const hasAnalyticsConsent = (): boolean => {
    return consentStatus === 'accepted'
  }

  /**
   * Verifica si hay un consentimiento registrado (aceptado o rechazado)
   */
  const hasConsent = (): boolean => {
    return consentStatus === 'accepted' || consentStatus === 'rejected'
  }

  /**
   * Acepta todas las cookies (analytics)
   */
  const acceptAll = (): void => {
    setStorageItem(STORAGE_KEYS.COOKIE_CONSENT, 'accepted')
    setConsentStatus('accepted')
    // El toque de la landing esperaba en memoria a este consentimiento.
    persistMarketingAttribution()
  }

  /**
   * Rechaza todas las cookies no esenciales
   */
  const rejectAll = (): void => {
    setStorageItem(STORAGE_KEYS.COOKIE_CONSENT, 'rejected')
    setConsentStatus('rejected')
    clearMarketingAttribution()
  }

  /**
   * Acepta solo cookies de analytics
   * (Por ahora solo tenemos analytics, pero esto permite extensibilidad)
   */
  const acceptAnalytics = (): void => {
    acceptAll() // Por ahora es lo mismo, pero mantiene la API consistente
  }

  /**
   * Resetea el consentimiento (útil para testing o si el usuario quiere cambiar su decisión)
   */
  const resetConsent = (): void => {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(STORAGE_KEYS.COOKIE_CONSENT)
      setConsentStatus('pending')
      // Toda transición que no acaba en «aceptado» borra la atribución guardada.
      clearMarketingAttribution()
    }
  }

  return {
    consentStatus,
    isLoading,
    hasConsent: hasConsent(),
    hasAnalyticsConsent: hasAnalyticsConsent(),
    acceptAll,
    rejectAll,
    acceptAnalytics,
    resetConsent,
  }
}













